// =============================================================================
// features/configuracoes/canais/VendedoresPage.tsx — Pareamento do WhatsApp dos
// vendedores (F39-S09).
//
// Lista os agentes e o estado do aparelho de cada um. Conectar abre o modal com
// o QR; desconectar encerra a sessão e apaga a credencial no WAHA.
//
// Gating: flag `whatsapp_sync.pairing.enabled` (nasce DESLIGADA). Sem ela, a
// página redireciona — a feature não deve nem existir para o operador antes do
// aceite formal sobre o risco de banimento.
//
// Mapeamento vendedor → canal: o backend grava `display_handle` = nome da
// sessão (`agente-<agentId>`) para provider `waha`. É por aí que a lista sabe
// quem já está pareado, sem precisar expor `waha_session_id` na API.
//
// DS doc 18: elev-2 nos cards, hover Lift, tokens canônicos, light + dark.
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { z } from 'zod';

import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { api } from '../../../lib/api';
import { cn } from '../../../lib/cn';

import {
  CONNECTION_GUARD_FLAG,
  CONNECTION_STATUS_LABEL,
  agentLineState,
  connectionTone,
  countNeedingAction,
  formatConnectionSince,
  sortForFollowUp,
  useAgentConnections,
  type AgentConnection,
  type ConnectionTone,
} from './agentConnections';
import { PairingModal } from './PairingModal';
import { useChannels } from './useChannels';
import {
  PAIRING_STATUS_LABEL,
  buildChannelBySession,
  sessionNameForAgent,
  useStartPairing,
  useStopPairing,
} from './useVendedorPairing';

// ---------------------------------------------------------------------------
// Agentes
// ---------------------------------------------------------------------------

const AgentSchema = z.object({
  id: z.string().uuid(),
  display_name: z.string(),
  is_active: z.boolean(),
});

const AgentListSchema = z.object({ data: z.array(AgentSchema) });

type Agent = z.infer<typeof AgentSchema>;

function useAgents(): ReturnType<typeof useQuery<Agent[]>> {
  return useQuery<Agent[]>({
    queryKey: ['admin-agents', 'pairing'],
    queryFn: async () => {
      const raw = await api.get('/api/admin/agents?limit=100');
      return AgentListSchema.parse(raw).data;
    },
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// F57-S03 — linha de status real
// ---------------------------------------------------------------------------

const TONE_COLOR: Record<ConnectionTone, string> = {
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  neutral: 'var(--text-2)',
};

/**
 * Obrigado: status real + "desde" + selo "Obrigatório". Não obrigado: a API
 * não acompanha — a linha diz só se há caixa cadastrada (sem afirmar
 * "Conectado", que era o que a tela dizia para qualquer caixa existente).
 */
function ConnectionLine({
  connection,
  isPaired,
  query,
}: {
  readonly connection: AgentConnection | undefined;
  readonly isPaired: boolean;
  readonly query: { readonly isLoading: boolean; readonly isError: boolean };
}): React.JSX.Element {
  const state = agentLineState(query, connection);
  if (state === 'unavailable') {
    // Carregando ou falhou: não sabemos quem é obrigado. Nunca "não obrigatório".
    return (
      <p className="font-sans text-xs text-ink-2 mt-0.5" role="status">
        Status indisponível
      </p>
    );
  }
  if (connection === undefined) {
    return (
      <p className="font-sans text-xs text-ink-2 mt-0.5">
        {isPaired ? 'Caixa cadastrada · não obrigatório' : 'Sem caixa · não obrigatório'}
      </p>
    );
  }
  const color = TONE_COLOR[connectionTone(connection)];
  const since = connection.status === 'WORKING' ? null : formatConnectionSince(connection.since);
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
      <span
        className="inline-flex items-center gap-1.5 font-sans text-xs font-semibold"
        style={{ color }}
      >
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 rounded-full"
          style={{ background: color, boxShadow: `0 0 6px ${color}` }}
        />
        {CONNECTION_STATUS_LABEL[connection.status]}
      </span>
      {since !== null ? (
        <span className="font-mono text-ink-2" style={{ fontSize: 'var(--text-xs)' }}>
          {since}
        </span>
      ) : null}
      <span
        className="font-sans font-bold uppercase rounded-full px-2 py-0.5 text-azul"
        style={{
          fontSize: '0.65rem',
          letterSpacing: '0.06em',
          background: 'var(--info-bg)',
          boxShadow: 'var(--elev-1)',
        }}
      >
        Obrigatório
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export function VendedoresPage(): React.JSX.Element {
  const { enabled: flagEnabled, isLoading: flagLoading } = useFeatureFlag(
    'whatsapp_sync.pairing.enabled',
  );

  const agentsQuery = useAgents();
  const channelsQuery = useChannels();

  // F57-S03: com a guarda ligada, status REAL de quem é obrigado. Sem a flag,
  // nenhuma chamada nova e a tela fica como antes.
  const { enabled: guardOn } = useFeatureFlag(CONNECTION_GUARD_FLAG);
  const connectionsQuery = useAgentConnections(guardOn);
  const connectionByAgent = React.useMemo(
    () => new Map((connectionsQuery.data ?? []).map((row) => [row.agent_id, row] as const)),
    [connectionsQuery.data],
  );
  const startPairing = useStartPairing();
  const stopPairing = useStopPairing();

  const [modal, setModal] = React.useState<{ channelId: string; agentName: string } | null>(null);

  /** sessão (`display_handle`) → channelId, só para canais WAHA vivos. */
  const channelBySession = React.useMemo(
    () => buildChannelBySession(channelsQuery.channels),
    [channelsQuery.channels],
  );

  if (flagLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]" role="status">
        <div className="w-6 h-6 rounded-full border-2 border-border-strong border-t-azul animate-spin" />
      </div>
    );
  }

  if (!flagEnabled) return <Navigate to="/404" replace />;

  async function handleConnect(agent: Agent): Promise<void> {
    const session = await startPairing.mutateAsync(agent.id);
    setModal({ channelId: session.channel_id, agentName: agent.display_name });
  }

  function handleDisconnect(agent: Agent, channelId: string): void {
    const ok = window.confirm(
      `Desconectar o WhatsApp de ${agent.display_name}?\n\n` +
        'As conversas já importadas continuam no sistema, mas novas mensagens ' +
        'deste número deixam de chegar.',
    );
    if (ok) stopPairing.mutate(channelId);
  }

  const activeAgents = (agentsQuery.data ?? []).filter((a) => a.is_active);
  const agents = guardOn
    ? sortForFollowUp(activeAgents, (a) => connectionByAgent.get(a.id))
    : activeAgents;
  const needingAction = guardOn ? countNeedingAction(connectionsQuery.data ?? []) : 0;

  return (
    <div
      className="flex flex-col gap-6"
      style={{ animation: 'fade-up var(--dur-slow) var(--ease-out)' }}
    >
      <div>
        <h1
          className="font-display font-bold text-ink leading-tight"
          style={{ fontSize: 'var(--text-3xl)', letterSpacing: '-0.04em' }}
        >
          WhatsApp dos vendedores
        </h1>
        <p className="font-sans text-sm text-ink-3 mt-1">
          Conecte o aparelho de cada vendedor para que as conversas entrem no sistema.
        </p>
      </div>

      {/* Aviso de risco — antes de qualquer botão de conectar. */}
      <div
        className="rounded-sm border border-warning/40 bg-warning-soft p-4"
        style={{ boxShadow: 'var(--elev-1)' }}
        role="note"
      >
        <p className="font-sans text-sm font-semibold text-ink">
          Antes de conectar, leia com atenção
        </p>
        <p className="font-sans text-sm text-ink-2 mt-1.5">
          Esta integração usa uma API não-oficial do WhatsApp. O número conectado{' '}
          <strong className="font-semibold">pode ser banido sem aviso e sem recurso</strong>, e é o
          número de trabalho do vendedor, com a carteira de clientes dele. Só conecte aparelhos com
          autorização formal.
        </p>
      </div>

      {/* F57-S03: resumo para cobrar — Alert do doc 18 §9.6. */}
      {guardOn && needingAction > 0 ? (
        <div
          role="status"
          className="rounded-sm px-4 py-3"
          style={{
            background: 'var(--danger-bg)',
            borderLeft: '3px solid var(--danger)',
            boxShadow: 'var(--elev-1)',
          }}
        >
          <p className="font-sans text-sm font-semibold" style={{ color: 'var(--danger)' }}>
            {needingAction === 1
              ? '1 vendedor obrigado está sem o WhatsApp conectado'
              : `${String(needingAction)} vendedores obrigados estão sem o WhatsApp conectado`}
          </p>
          <p className="font-sans text-sm text-ink-2 mt-1">
            Enquanto isso, as mensagens dos clientes deles não chegam ao sistema. Eles veem um aviso
            com o passo a passo ao abrir o sistema.
          </p>
        </div>
      ) : null}

      {/* Lista */}
      <div
        className="rounded-md border border-border bg-surface-1 overflow-hidden"
        style={{ boxShadow: 'var(--elev-2)' }}
      >
        {agentsQuery.isLoading ? (
          <div className="p-8 flex items-center justify-center" role="status">
            <div className="w-6 h-6 rounded-full border-2 border-border-strong border-t-azul animate-spin" />
          </div>
        ) : agents.length === 0 ? (
          <p className="p-8 text-center font-sans text-sm text-ink-3">
            Nenhum vendedor ativo cadastrado.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {agents.map((agent) => {
              const channelId = channelBySession.get(sessionNameForAgent(agent.id));
              const isPaired = channelId !== undefined;

              return (
                <li
                  key={agent.id}
                  className="flex items-center justify-between gap-4 px-5 py-4 transition-colors duration-fast hover:bg-surface-hover"
                >
                  <div className="min-w-0">
                    <p className="font-sans text-sm font-semibold text-ink truncate">
                      {agent.display_name}
                    </p>
                    {guardOn ? (
                      <ConnectionLine
                        connection={connectionByAgent.get(agent.id)}
                        isPaired={isPaired}
                        query={connectionsQuery}
                      />
                    ) : (
                      <p className="font-sans text-xs text-ink-3 mt-0.5">
                        {isPaired ? PAIRING_STATUS_LABEL.WORKING : PAIRING_STATUS_LABEL.STOPPED}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {isPaired ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setModal({ channelId, agentName: agent.display_name });
                          }}
                          className={cn(
                            'font-sans text-sm font-medium px-3 py-2 rounded-sm',
                            'border border-border-strong text-ink',
                            'transition-colors duration-fast hover:bg-surface-hover',
                          )}
                        >
                          Ver status
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            handleDisconnect(agent, channelId);
                          }}
                          disabled={stopPairing.isPending}
                          className={cn(
                            'font-sans text-sm font-medium px-3 py-2 rounded-sm',
                            'border border-danger/40 text-danger',
                            'transition-colors duration-fast hover:bg-danger-soft',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                          )}
                        >
                          Desconectar
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void handleConnect(agent)}
                        disabled={startPairing.isPending}
                        className={cn(
                          'font-sans text-sm font-semibold px-4 py-2 rounded-sm',
                          'bg-azul text-white',
                          'transition-[background,transform] duration-fast hover:brightness-110',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                        )}
                      >
                        Conectar
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <PairingModal
        open={modal !== null}
        channelId={modal?.channelId ?? null}
        agentName={modal?.agentName ?? ''}
        onClose={() => {
          setModal(null);
        }}
      />
    </div>
  );
}
